import { colours, colourTokens, flattenObjectToArray } from '@/styles/colours';

import type {
  BackgroundColourType,
  CombinedTokenType,
  DecorativeColourType,
  SurfaceColourType,
} from '@/types/colours';

const lightBackgroundColours = [
  '--background-subtle',
  '--background-subtle',
  '--background-warm-base',
  '--background-warm-strong',
  '--background-cool-base',
  '--background-cool-strong',
  '--surface-subtle',
  '--surface-warm-base',
  '--surface-warm-strong',
  '--surface-cool-base',
  '--surface-cool-strong',
  '--surface-primary-subtle',
  '--surface-danger-subtle',
  '--decorative-starlight',
  '--decorative-eclipse',
  '--decorative-dark-eclipse',
  '--decorative-dark-sunbeam',
  '--decorative-sunbeam',
  '--decorative-green-tint',
  '--decorative-blue-tint',
  '--decorative-yellow',
  '--decorative-yellow-tint',
];

const darkBackgroundColours = [
  '--background-primary',
  '--surface-inverse-subtle',
  '--surface-inverse-base',
  '--surface-inverse-strong',
  '--surface-primary',
  '--surface-inverse-primary',
  '--surface-danger-strong',
  '--surface-inverse-danger',
  '--decorative-ultraviolet',
  '--decorative-dark-ultraviolet',
  '--decorative-galaxy',
  '--decorative-soft-galaxy',
  '--decorative-green',
  '--decorative-red',
  '--decorative-red-tint',
  '--decorative-blue',
];

export const getBulletPointColour = <
  T extends
    | SurfaceColourType['token']
    | BackgroundColourType['token']
    | DecorativeColourType['token'],
>(
  backgroundColour: T,
) => {
  if (lightBackgroundColours.includes(backgroundColour)) {
    return '--decorative-ultraviolet';
  } else if (darkBackgroundColours.includes(backgroundColour)) {
    return '--icon-inverse-warm-base';
  }
};

export const getTextColour = <
  T extends
    | SurfaceColourType['token']
    | BackgroundColourType['token']
    | DecorativeColourType['token'],
>(
  backgroundColour: T,
  options?: { action?: boolean; isCaption?: boolean },
) => {
  if (backgroundColour == '--surface-inverse-subtle') {
    return '--text-eclipse';
  } else if (lightBackgroundColours.includes(backgroundColour)) {
    if (options?.action || options?.isCaption) {
      return '--text-action';
    }
    return '--text-strong';
  } else if (darkBackgroundColours.includes(backgroundColour)) {
    if (options?.action) {
      return '--text-inverse-action';
    }
    return '--text-inverse-strong';
  }
};

export const isLightBackground = <
  T extends
    | SurfaceColourType['token']
    | BackgroundColourType['token']
    | DecorativeColourType['token'],
>(
  backgroundColour: T,
): boolean => {
  return lightBackgroundColours.includes(backgroundColour);
};

export const isDarkBackground = <
  T extends
    | SurfaceColourType['token']
    | BackgroundColourType['token']
    | DecorativeColourType['token'],
>(
  backgroundColour: T,
): boolean => {
  return darkBackgroundColours.includes(backgroundColour);
};

export const getButtonComponentTheme = <
  T extends
    | SurfaceColourType['token']
    | BackgroundColourType['token']
    | DecorativeColourType['token'],
>(
  backgroundColour: T,
): 'light' | 'dark' => {
  if (
    isDarkBackground(backgroundColour) ||
    backgroundColour === '--background-primary' ||
    backgroundColour === '--surface-primary'
  ) {
    return 'dark';
  }
  return 'light';
};

export const getColourFromToken = (token: CombinedTokenType) => {
  const flatTokens = flattenObjectToArray(colourTokens);
  const flatColours = flattenObjectToArray(colours);

  return [...flatTokens, ...flatColours].find((item) => item.token === token)
    .colour;
};
